import {
  IconLayoutGrid,
  IconLayoutColumns,
  IconLayoutRows,
  IconLayout2,
  IconLayoutBoard,
  IconLayoutSidebar,
  IconLayoutBottombar,
  IconLayoutNavbar,
  IconSquaresFilled,
  IconLayoutDashboard,
} from '@tabler/icons-react';
import { registerContainer } from '../containerRegistry';

// ============================================================================
// Pre-defined Container Templates
// ============================================================================

// 2×2 Grid Container (for 4 stat widgets)
registerContainer({
  id: 'grid-2x2',
  name: '2×2 Grid',
  description: 'Four equal cells arranged in a 2×2 grid',
  icon: IconLayoutGrid,
  layout: '2x2-grid',
  sizes: {
    base: { cols: 12, rows: 8 },  // Full width on mobile, stacked vertically
    sm: { cols: 12, rows: 4 },    // 2×2 arrangement
    md: { cols: 6, rows: 4 },
    lg: { cols: 6, rows: 4 },
    xl: { cols: 6, rows: 4 },
    tv: { cols: 4, rows: 4 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 6, rows: 3 },
        lg: { cols: 4, rows: 3 },
        xl: { cols: 4, rows: 3 },
        tv: { cols: 4, rows: 3 },
      },
      standard: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 6, rows: 4 },
        lg: { cols: 6, rows: 4 },
        xl: { cols: 6, rows: 4 },
        tv: { cols: 4, rows: 4 },
      },
      wide: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 8, rows: 4 },
        lg: { cols: 8, rows: 4 },
        xl: { cols: 8, rows: 4 },
        tv: { cols: 6, rows: 4 },
      },
    },
  },
  internalGrid: { cols: 2, rows: 2 },
  slots: [
    { id: 'top-left', col: 0, row: 0 },
    { id: 'top-right', col: 1, row: 0 },
    { id: 'bottom-left', col: 0, row: 1 },
    { id: 'bottom-right', col: 1, row: 1 },
  ],
});

// 1×2 Row Container (for 2 horizontal widgets)
registerContainer({
  id: 'row-1x2',
  name: '1×2 Row',
  description: 'Two equal cells in a horizontal row',
  icon: IconLayoutColumns,
  layout: '1x2-row',
  sizes: {
    base: { cols: 12, rows: 4 },  // Stack vertically on mobile
    sm: { cols: 12, rows: 2 },
    md: { cols: 8, rows: 2 },
    lg: { cols: 6, rows: 2 },
    xl: { cols: 6, rows: 2 },
    tv: { cols: 4, rows: 2 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 8, rows: 2 },
        md: { cols: 6, rows: 2 },
        lg: { cols: 4, rows: 2 },
        xl: { cols: 4, rows: 2 },
        tv: { cols: 4, rows: 2 },
      },
      standard: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 12, rows: 2 },
        md: { cols: 8, rows: 2 },
        lg: { cols: 6, rows: 2 },
        xl: { cols: 6, rows: 2 },
        tv: { cols: 4, rows: 2 },
      },
      wide: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 12, rows: 2 },
        md: { cols: 12, rows: 2 },
        lg: { cols: 8, rows: 2 },
        xl: { cols: 8, rows: 2 },
        tv: { cols: 6, rows: 2 },
      },
    },
  },
  internalGrid: { cols: 2, rows: 1 },
  slots: [
    { id: 'left', col: 0, row: 0 },
    { id: 'right', col: 1, row: 0 },
  ],
});

// 1×3 Row Container (for 3 horizontal widgets)
registerContainer({
  id: 'row-1x3',
  name: '1×3 Row',
  description: 'Three equal cells in a horizontal row',
  icon: IconLayoutColumns,
  layout: '1x3-row',
  sizes: {
    base: { cols: 12, rows: 6 },  // Stack vertically on mobile
    sm: { cols: 12, rows: 2 },
    md: { cols: 12, rows: 2 },
    lg: { cols: 9, rows: 2 },
    xl: { cols: 9, rows: 2 },
    tv: { cols: 6, rows: 2 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 2 },
        md: { cols: 9, rows: 2 },
        lg: { cols: 6, rows: 2 },
        xl: { cols: 6, rows: 2 },
        tv: { cols: 6, rows: 2 },
      },
      standard: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 2 },
        md: { cols: 12, rows: 2 },
        lg: { cols: 9, rows: 2 },
        xl: { cols: 9, rows: 2 },
        tv: { cols: 6, rows: 2 },
      },
      wide: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 2 },
        md: { cols: 12, rows: 2 },
        lg: { cols: 12, rows: 2 },
        xl: { cols: 12, rows: 2 },
        tv: { cols: 9, rows: 2 },
      },
    },
  },
  internalGrid: { cols: 3, rows: 1 },
  slots: [
    { id: 'left', col: 0, row: 0 },
    { id: 'center', col: 1, row: 0 },
    { id: 'right', col: 2, row: 0 },
  ],
});

// 1×4 Row Container (for 4 horizontal widgets)
registerContainer({
  id: 'row-1x4',
  name: '1×4 Row',
  description: 'Four equal cells in a horizontal row',
  icon: IconLayoutColumns,
  layout: '1x4-row',
  sizes: {
    base: { cols: 12, rows: 8 },  // Stack on mobile
    sm: { cols: 12, rows: 4 },    // 2×2 on small tablets
    md: { cols: 12, rows: 2 },
    lg: { cols: 12, rows: 2 },
    xl: { cols: 12, rows: 2 },
    tv: { cols: 8, rows: 2 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 8, rows: 2 },
        lg: { cols: 8, rows: 2 },
        xl: { cols: 8, rows: 2 },
        tv: { cols: 8, rows: 2 },
      },
      standard: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 12, rows: 2 },
        lg: { cols: 12, rows: 2 },
        xl: { cols: 12, rows: 2 },
        tv: { cols: 8, rows: 2 },
      },
      wide: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 12, rows: 2 },
        lg: { cols: 12, rows: 2 },
        xl: { cols: 12, rows: 2 },
        tv: { cols: 12, rows: 2 },
      },
    },
  },
  internalGrid: { cols: 4, rows: 1 },
  slots: [
    { id: 'slot-1', col: 0, row: 0 },
    { id: 'slot-2', col: 1, row: 0 },
    { id: 'slot-3', col: 2, row: 0 },
    { id: 'slot-4', col: 3, row: 0 },
  ],
});

// 2×1 Column Container (for 2 vertical widgets)
registerContainer({
  id: 'col-2x1',
  name: '2×1 Column',
  description: 'Two cells stacked vertically',
  icon: IconLayoutRows,
  layout: '2x1-col',
  sizes: {
    base: { cols: 12, rows: 6 },
    sm: { cols: 6, rows: 6 },
    md: { cols: 4, rows: 6 },
    lg: { cols: 4, rows: 6 },
    xl: { cols: 3, rows: 6 },
    tv: { cols: 3, rows: 6 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'tall'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 6, rows: 4 },
        md: { cols: 4, rows: 4 },
        lg: { cols: 3, rows: 4 },
        xl: { cols: 3, rows: 4 },
        tv: { cols: 2, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 6, rows: 6 },
        md: { cols: 4, rows: 6 },
        lg: { cols: 4, rows: 6 },
        xl: { cols: 3, rows: 6 },
        tv: { cols: 3, rows: 6 },
      },
      tall: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 6, rows: 8 },
        md: { cols: 4, rows: 8 },
        lg: { cols: 4, rows: 8 },
        xl: { cols: 3, rows: 8 },
        tv: { cols: 3, rows: 8 },
      },
    },
  },
  internalGrid: { cols: 1, rows: 2 },
  slots: [
    { id: 'top', col: 0, row: 0 },
    { id: 'bottom', col: 0, row: 1 },
  ],
});

// 3×2 Grid Container (for 6 widgets)
registerContainer({
  id: 'grid-3x2',
  name: '3×2 Grid',
  description: 'Six cells arranged in a 3×2 grid',
  icon: IconLayoutBoard,
  layout: '3x2-grid',
  sizes: {
    base: { cols: 12, rows: 12 },  // Stack on mobile
    sm: { cols: 12, rows: 6 },     // 2 rows of 3
    md: { cols: 12, rows: 4 },
    lg: { cols: 9, rows: 4 },
    xl: { cols: 9, rows: 4 },
    tv: { cols: 6, rows: 4 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 12 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 9, rows: 4 },
        lg: { cols: 6, rows: 4 },
        xl: { cols: 6, rows: 4 },
        tv: { cols: 6, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 12 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 12, rows: 4 },
        lg: { cols: 9, rows: 4 },
        xl: { cols: 9, rows: 4 },
        tv: { cols: 6, rows: 4 },
      },
      wide: {
        base: { cols: 12, rows: 12 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 12, rows: 4 },
        lg: { cols: 12, rows: 4 },
        xl: { cols: 12, rows: 4 },
        tv: { cols: 9, rows: 4 },
      },
    },
  },
  internalGrid: { cols: 3, rows: 2 },
  slots: [
    { id: 'top-left', col: 0, row: 0 },
    { id: 'top-center', col: 1, row: 0 },
    { id: 'top-right', col: 2, row: 0 },
    { id: 'bottom-left', col: 0, row: 1 },
    { id: 'bottom-center', col: 1, row: 1 },
    { id: 'bottom-right', col: 2, row: 1 },
  ],
});

// 2×3 Grid Container (for 6 widgets in portrait orientation)
registerContainer({
  id: 'grid-2x3',
  name: '2×3 Grid',
  description: 'Six cells arranged in a 2×3 grid (portrait)',
  icon: IconLayout2,
  layout: 'flexible',
  sizes: {
    base: { cols: 12, rows: 12 },  // Stack on mobile
    sm: { cols: 12, rows: 6 },
    md: { cols: 8, rows: 6 },
    lg: { cols: 6, rows: 6 },
    xl: { cols: 6, rows: 6 },
    tv: { cols: 4, rows: 6 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 12 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 6, rows: 6 },
        lg: { cols: 4, rows: 6 },
        xl: { cols: 4, rows: 6 },
        tv: { cols: 4, rows: 6 },
      },
      standard: {
        base: { cols: 12, rows: 12 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 8, rows: 6 },
        lg: { cols: 6, rows: 6 },
        xl: { cols: 6, rows: 6 },
        tv: { cols: 4, rows: 6 },
      },
      wide: {
        base: { cols: 12, rows: 12 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 8, rows: 6 },
        xl: { cols: 8, rows: 6 },
        tv: { cols: 6, rows: 6 },
      },
    },
  },
  internalGrid: { cols: 2, rows: 3 },
  slots: [
    { id: 'row1-left', col: 0, row: 0 },
    { id: 'row1-right', col: 1, row: 0 },
    { id: 'row2-left', col: 0, row: 1 },
    { id: 'row2-right', col: 1, row: 1 },
    { id: 'row3-left', col: 0, row: 2 },
    { id: 'row3-right', col: 1, row: 2 },
  ],
});

// ============================================================================
// Full-Width Containers
// ============================================================================

// Full-width 1×2 Row (spans entire dashboard width)
registerContainer({
  id: 'row-full-1x2',
  name: 'Full Width 1×2',
  description: 'Two cells spanning full dashboard width',
  icon: IconLayoutNavbar,
  layout: '1x2-row',
  sizes: {
    base: { cols: 12, rows: 4 },
    sm: { cols: 12, rows: 3 },
    md: { cols: 12, rows: 3 },
    lg: { cols: 12, rows: 3 },
    xl: { cols: 12, rows: 3 },
    tv: { cols: 12, rows: 2 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'tall'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 12, rows: 2 },
        md: { cols: 12, rows: 2 },
        lg: { cols: 12, rows: 2 },
        xl: { cols: 12, rows: 2 },
        tv: { cols: 12, rows: 2 },
      },
      standard: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 12, rows: 3 },
        md: { cols: 12, rows: 3 },
        lg: { cols: 12, rows: 3 },
        xl: { cols: 12, rows: 3 },
        tv: { cols: 12, rows: 2 },
      },
      tall: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 12, rows: 4 },
        lg: { cols: 12, rows: 4 },
        xl: { cols: 12, rows: 4 },
        tv: { cols: 12, rows: 3 },
      },
    },
  },
  internalGrid: { cols: 2, rows: 1 },
  slots: [
    { id: 'left', col: 0, row: 0 },
    { id: 'right', col: 1, row: 0 },
  ],
});

// Full-width 4×1 Grid (4 stat cards in a row)
registerContainer({
  id: 'grid-full-4x1',
  name: 'Full Width 4×1',
  description: 'Four cells in a full-width row, perfect for stat cards',
  icon: IconSquaresFilled,
  layout: '1x4-row',
  sizes: {
    base: { cols: 12, rows: 8 },
    sm: { cols: 12, rows: 4 },
    md: { cols: 12, rows: 3 },
    lg: { cols: 12, rows: 3 },
    xl: { cols: 12, rows: 3 },
    tv: { cols: 12, rows: 2 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'tall'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 12, rows: 2 },
        lg: { cols: 12, rows: 2 },
        xl: { cols: 12, rows: 2 },
        tv: { cols: 12, rows: 2 },
      },
      standard: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 12, rows: 3 },
        lg: { cols: 12, rows: 3 },
        xl: { cols: 12, rows: 3 },
        tv: { cols: 12, rows: 2 },
      },
      tall: {
        base: { cols: 12, rows: 12 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 12, rows: 4 },
        lg: { cols: 12, rows: 4 },
        xl: { cols: 12, rows: 4 },
        tv: { cols: 12, rows: 3 },
      },
    },
  },
  internalGrid: { cols: 4, rows: 1 },
  slots: [
    { id: 'slot-1', col: 0, row: 0 },
    { id: 'slot-2', col: 1, row: 0 },
    { id: 'slot-3', col: 2, row: 0 },
    { id: 'slot-4', col: 3, row: 0 },
  ],
});

// ============================================================================
// Sidebar Layout Containers
// ============================================================================

// Sidebar left (1 wide + 1 narrow)
registerContainer({
  id: 'sidebar-left',
  name: 'Sidebar Left',
  description: 'Wide content area with narrow sidebar on left',
  icon: IconLayoutSidebar,
  layout: 'flexible',
  sizes: {
    base: { cols: 12, rows: 8 },
    sm: { cols: 12, rows: 6 },
    md: { cols: 12, rows: 5 },
    lg: { cols: 12, rows: 5 },
    xl: { cols: 12, rows: 5 },
    tv: { cols: 12, rows: 4 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'tall'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 12, rows: 4 },
        lg: { cols: 12, rows: 4 },
        xl: { cols: 12, rows: 4 },
        tv: { cols: 12, rows: 3 },
      },
      standard: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 12, rows: 5 },
        lg: { cols: 12, rows: 5 },
        xl: { cols: 12, rows: 5 },
        tv: { cols: 12, rows: 4 },
      },
      tall: {
        base: { cols: 12, rows: 10 },
        sm: { cols: 12, rows: 8 },
        md: { cols: 12, rows: 7 },
        lg: { cols: 12, rows: 7 },
        xl: { cols: 12, rows: 7 },
        tv: { cols: 12, rows: 6 },
      },
    },
  },
  internalGrid: { cols: 3, rows: 1 },
  slots: [
    { id: 'sidebar', col: 0, row: 0 },
    { id: 'content', col: 1, row: 0, colSpan: 2 },
  ],
});

// Sidebar right (1 narrow + 1 wide)
registerContainer({
  id: 'sidebar-right',
  name: 'Sidebar Right',
  description: 'Wide content area with narrow sidebar on right',
  icon: IconLayoutSidebar,
  layout: 'flexible',
  sizes: {
    base: { cols: 12, rows: 8 },
    sm: { cols: 12, rows: 6 },
    md: { cols: 12, rows: 5 },
    lg: { cols: 12, rows: 5 },
    xl: { cols: 12, rows: 5 },
    tv: { cols: 12, rows: 4 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'tall'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 12, rows: 4 },
        lg: { cols: 12, rows: 4 },
        xl: { cols: 12, rows: 4 },
        tv: { cols: 12, rows: 3 },
      },
      standard: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 12, rows: 5 },
        lg: { cols: 12, rows: 5 },
        xl: { cols: 12, rows: 5 },
        tv: { cols: 12, rows: 4 },
      },
      tall: {
        base: { cols: 12, rows: 10 },
        sm: { cols: 12, rows: 8 },
        md: { cols: 12, rows: 7 },
        lg: { cols: 12, rows: 7 },
        xl: { cols: 12, rows: 7 },
        tv: { cols: 12, rows: 6 },
      },
    },
  },
  internalGrid: { cols: 3, rows: 1 },
  slots: [
    { id: 'content', col: 0, row: 0, colSpan: 2 },
    { id: 'sidebar', col: 2, row: 0 },
  ],
});

// ============================================================================
// Header/Footer Layout Containers
// ============================================================================

// Header + Content (top bar with main content below)
registerContainer({
  id: 'header-content',
  name: 'Header + Content',
  description: 'Small header area with larger content below',
  icon: IconLayoutNavbar,
  layout: 'flexible',
  sizes: {
    base: { cols: 12, rows: 8 },
    sm: { cols: 12, rows: 6 },
    md: { cols: 8, rows: 6 },
    lg: { cols: 8, rows: 6 },
    xl: { cols: 6, rows: 6 },
    tv: { cols: 6, rows: 5 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 8, rows: 5 },
        md: { cols: 6, rows: 5 },
        lg: { cols: 6, rows: 5 },
        xl: { cols: 4, rows: 5 },
        tv: { cols: 4, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 8, rows: 6 },
        lg: { cols: 8, rows: 6 },
        xl: { cols: 6, rows: 6 },
        tv: { cols: 6, rows: 5 },
      },
      wide: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 12, rows: 6 },
        xl: { cols: 12, rows: 6 },
        tv: { cols: 12, rows: 5 },
      },
    },
  },
  internalGrid: { cols: 1, rows: 3 },
  slots: [
    { id: 'header', col: 0, row: 0 },
    { id: 'content', col: 0, row: 1, rowSpan: 2 },
  ],
});

// Content + Footer (main content with bottom bar)
registerContainer({
  id: 'content-footer',
  name: 'Content + Footer',
  description: 'Larger content area with small footer below',
  icon: IconLayoutBottombar,
  layout: 'flexible',
  sizes: {
    base: { cols: 12, rows: 8 },
    sm: { cols: 12, rows: 6 },
    md: { cols: 8, rows: 6 },
    lg: { cols: 8, rows: 6 },
    xl: { cols: 6, rows: 6 },
    tv: { cols: 6, rows: 5 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 8, rows: 5 },
        md: { cols: 6, rows: 5 },
        lg: { cols: 6, rows: 5 },
        xl: { cols: 4, rows: 5 },
        tv: { cols: 4, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 8, rows: 6 },
        lg: { cols: 8, rows: 6 },
        xl: { cols: 6, rows: 6 },
        tv: { cols: 6, rows: 5 },
      },
      wide: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 12, rows: 6 },
        xl: { cols: 12, rows: 6 },
        tv: { cols: 12, rows: 5 },
      },
    },
  },
  internalGrid: { cols: 1, rows: 3 },
  slots: [
    { id: 'content', col: 0, row: 0, rowSpan: 2 },
    { id: 'footer', col: 0, row: 2 },
  ],
});

// ============================================================================
// Mixed Layout Containers
// ============================================================================

// 3×1 Column (3 stacked vertically)
registerContainer({
  id: 'col-3x1',
  name: '3×1 Column',
  description: 'Three cells stacked vertically',
  icon: IconLayoutRows,
  layout: 'flexible',
  sizes: {
    base: { cols: 12, rows: 9 },
    sm: { cols: 6, rows: 9 },
    md: { cols: 4, rows: 9 },
    lg: { cols: 4, rows: 9 },
    xl: { cols: 3, rows: 9 },
    tv: { cols: 3, rows: 9 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'tall'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 6, rows: 6 },
        md: { cols: 4, rows: 6 },
        lg: { cols: 3, rows: 6 },
        xl: { cols: 3, rows: 6 },
        tv: { cols: 2, rows: 6 },
      },
      standard: {
        base: { cols: 12, rows: 9 },
        sm: { cols: 6, rows: 9 },
        md: { cols: 4, rows: 9 },
        lg: { cols: 4, rows: 9 },
        xl: { cols: 3, rows: 9 },
        tv: { cols: 3, rows: 9 },
      },
      tall: {
        base: { cols: 12, rows: 12 },
        sm: { cols: 6, rows: 12 },
        md: { cols: 4, rows: 12 },
        lg: { cols: 4, rows: 12 },
        xl: { cols: 3, rows: 12 },
        tv: { cols: 3, rows: 12 },
      },
    },
  },
  internalGrid: { cols: 1, rows: 3 },
  slots: [
    { id: 'top', col: 0, row: 0 },
    { id: 'middle', col: 0, row: 1 },
    { id: 'bottom', col: 0, row: 2 },
  ],
});

// Dashboard Layout (2 top + 1 wide bottom)
registerContainer({
  id: 'dashboard-2-1',
  name: 'Dashboard 2+1',
  description: 'Two widgets on top, one wide widget on bottom',
  icon: IconLayoutDashboard,
  layout: 'flexible',
  sizes: {
    base: { cols: 12, rows: 10 },
    sm: { cols: 12, rows: 8 },
    md: { cols: 12, rows: 6 },
    lg: { cols: 8, rows: 6 },
    xl: { cols: 8, rows: 6 },
    tv: { cols: 8, rows: 5 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 8, rows: 5 },
        lg: { cols: 6, rows: 5 },
        xl: { cols: 6, rows: 5 },
        tv: { cols: 6, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 10 },
        sm: { cols: 12, rows: 8 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 8, rows: 6 },
        xl: { cols: 8, rows: 6 },
        tv: { cols: 8, rows: 5 },
      },
      wide: {
        base: { cols: 12, rows: 10 },
        sm: { cols: 12, rows: 8 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 12, rows: 6 },
        xl: { cols: 12, rows: 6 },
        tv: { cols: 12, rows: 5 },
      },
    },
  },
  internalGrid: { cols: 2, rows: 2 },
  slots: [
    { id: 'top-left', col: 0, row: 0 },
    { id: 'top-right', col: 1, row: 0 },
    { id: 'bottom', col: 0, row: 1, colSpan: 2 },
  ],
});

// Dashboard Layout (1 wide top + 2 bottom)
registerContainer({
  id: 'dashboard-1-2',
  name: 'Dashboard 1+2',
  description: 'One wide widget on top, two widgets on bottom',
  icon: IconLayoutDashboard,
  layout: 'flexible',
  sizes: {
    base: { cols: 12, rows: 10 },
    sm: { cols: 12, rows: 8 },
    md: { cols: 12, rows: 6 },
    lg: { cols: 8, rows: 6 },
    xl: { cols: 8, rows: 6 },
    tv: { cols: 8, rows: 5 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 8, rows: 5 },
        lg: { cols: 6, rows: 5 },
        xl: { cols: 6, rows: 5 },
        tv: { cols: 6, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 10 },
        sm: { cols: 12, rows: 8 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 8, rows: 6 },
        xl: { cols: 8, rows: 6 },
        tv: { cols: 8, rows: 5 },
      },
      wide: {
        base: { cols: 12, rows: 10 },
        sm: { cols: 12, rows: 8 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 12, rows: 6 },
        xl: { cols: 12, rows: 6 },
        tv: { cols: 12, rows: 5 },
      },
    },
  },
  internalGrid: { cols: 2, rows: 2 },
  slots: [
    { id: 'top', col: 0, row: 0, colSpan: 2 },
    { id: 'bottom-left', col: 0, row: 1 },
    { id: 'bottom-right', col: 1, row: 1 },
  ],
});

// 4×2 Grid (8 widgets)
registerContainer({
  id: 'grid-4x2',
  name: '4×2 Grid',
  description: 'Eight cells arranged in a 4×2 grid',
  icon: IconLayoutBoard,
  layout: 'flexible',
  sizes: {
    base: { cols: 12, rows: 8 },
    sm: { cols: 12, rows: 6 },
    md: { cols: 12, rows: 4 },
    lg: { cols: 12, rows: 4 },
    xl: { cols: 12, rows: 4 },
    tv: { cols: 12, rows: 3 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'tall'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 12, rows: 3 },
        lg: { cols: 12, rows: 3 },
        xl: { cols: 12, rows: 3 },
        tv: { cols: 12, rows: 2 },
      },
      standard: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 12, rows: 4 },
        lg: { cols: 12, rows: 4 },
        xl: { cols: 12, rows: 4 },
        tv: { cols: 12, rows: 3 },
      },
      tall: {
        base: { cols: 12, rows: 12 },
        sm: { cols: 12, rows: 8 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 12, rows: 6 },
        xl: { cols: 12, rows: 6 },
        tv: { cols: 12, rows: 4 },
      },
    },
  },
  internalGrid: { cols: 4, rows: 2 },
  slots: [
    { id: 'row1-1', col: 0, row: 0 },
    { id: 'row1-2', col: 1, row: 0 },
    { id: 'row1-3', col: 2, row: 0 },
    { id: 'row1-4', col: 3, row: 0 },
    { id: 'row2-1', col: 0, row: 1 },
    { id: 'row2-2', col: 1, row: 1 },
    { id: 'row2-3', col: 2, row: 1 },
    { id: 'row2-4', col: 3, row: 1 },
  ],
});

// L-Shape Layout (1 large + 2 small)
registerContainer({
  id: 'l-shape',
  name: 'L-Shape',
  description: 'One large widget with two small widgets in an L pattern',
  icon: IconLayout2,
  layout: 'flexible',
  sizes: {
    base: { cols: 12, rows: 8 },
    sm: { cols: 12, rows: 6 },
    md: { cols: 8, rows: 6 },
    lg: { cols: 6, rows: 6 },
    xl: { cols: 6, rows: 6 },
    tv: { cols: 6, rows: 5 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 6, rows: 4 },
        lg: { cols: 4, rows: 4 },
        xl: { cols: 4, rows: 4 },
        tv: { cols: 4, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 8, rows: 6 },
        lg: { cols: 6, rows: 6 },
        xl: { cols: 6, rows: 6 },
        tv: { cols: 6, rows: 5 },
      },
      wide: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 9, rows: 6 },
        xl: { cols: 9, rows: 6 },
        tv: { cols: 8, rows: 5 },
      },
    },
  },
  internalGrid: { cols: 2, rows: 2 },
  slots: [
    { id: 'main', col: 0, row: 0, rowSpan: 2 },
    { id: 'top-right', col: 1, row: 0 },
    { id: 'bottom-right', col: 1, row: 1 },
  ],
});

// T-Shape Layout (1 wide top + 3 bottom)
registerContainer({
  id: 't-shape',
  name: 'T-Shape',
  description: 'One wide widget on top with three below',
  icon: IconLayoutBoard,
  layout: 'flexible',
  sizes: {
    base: { cols: 12, rows: 10 },
    sm: { cols: 12, rows: 8 },
    md: { cols: 12, rows: 6 },
    lg: { cols: 9, rows: 6 },
    xl: { cols: 9, rows: 6 },
    tv: { cols: 9, rows: 5 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 9, rows: 5 },
        lg: { cols: 6, rows: 5 },
        xl: { cols: 6, rows: 5 },
        tv: { cols: 6, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 10 },
        sm: { cols: 12, rows: 8 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 9, rows: 6 },
        xl: { cols: 9, rows: 6 },
        tv: { cols: 9, rows: 5 },
      },
      wide: {
        base: { cols: 12, rows: 10 },
        sm: { cols: 12, rows: 8 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 12, rows: 6 },
        xl: { cols: 12, rows: 6 },
        tv: { cols: 12, rows: 5 },
      },
    },
  },
  internalGrid: { cols: 3, rows: 2 },
  slots: [
    { id: 'top', col: 0, row: 0, colSpan: 3 },
    { id: 'bottom-left', col: 0, row: 1 },
    { id: 'bottom-center', col: 1, row: 1 },
    { id: 'bottom-right', col: 2, row: 1 },
  ],
});
